import React, { FC, useEffect, useMemo, useState } from 'react'
import { TabItemWrapper, TabWrapper } from './style'
import MenuTab, { MenuTabItem } from '@berlitzplatforms/micro.ui.menu-tab/lib'
import { FormattedMessage } from 'react-intl'
import { StoreProps } from '@interfaces/StoreState'
import PortalStudentProfile from '@classes/StudentProfile'
import { useSelector } from 'react-redux'
import Layout from '@layouts/LearningPath/Layout'
import { LPContextProvider } from './Context/LPContext'
import { getStoredMaterials, getStoredPrograms, getStoredRegistrations } from '@utils/helpers/material'
import MyBerlitzTutorLayout from './MyBerlitzTutorLayout'
import Spacer from '@berlitz/spacer'
import QueryLearningPath from '../QueryLearningPathv2'
import QueryLearningPathISC from '../QueryLearningPathISC'
import Loader from './Loader'
import SkillsTutorTab from '@features/skillsTutor/pages'
import { REG_STATUS } from '@utils/constants'
import { MATERIAL_LEVEL } from '@utils/constants/learningPath'

interface ParentLayout {
  onTabChange: () => void
}

const ParentLayout: FC = () => {
  const { studentProfile, activeMaterial, activeProgram, activeRegistration, isStudent } = useSelector(
    ({ userProfile, dashboard }: StoreProps) => ({
      studentProfile: new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo),
      activeMaterial: dashboard.activeMaterial || undefined,
      activeProgram: dashboard.activeProgram || undefined,
      activeRegistration: dashboard.activeRegistration || undefined,
      isStudent: userProfile?.info?.Type === 'Student',
    })
  )
  const ActiveIdFromMaterialDropdown = studentProfile?.getActiveIdFromMaterialDropdown()
  const activeDropdownLPId = ActiveIdFromMaterialDropdown?.materialLPID
  const activeDropdownRegistrationId = ActiveIdFromMaterialDropdown?.registrationId
  const activeDropdownProgramId = ActiveIdFromMaterialDropdown?.programId
  const userSelectedMaterial = getStoredMaterials()?.[studentProfile.SFId]
  const userSelectedRegistrationId = getStoredRegistrations()?.[studentProfile.SFId]
  const userSelectedProgramId = getStoredPrograms()?.[studentProfile.SFId]
  const selected = activeMaterial || userSelectedMaterial || activeDropdownLPId
  const selectedProgramId = activeProgram || userSelectedProgramId || activeDropdownProgramId
  const selectedRegistrationId = activeRegistration || userSelectedRegistrationId || activeDropdownRegistrationId
  const hasActiveIndSpecificContent = studentProfile.hasActiveIndSpecificContent

  const hasSkillTutorPermission = studentProfile.SkillsTutorTabFlag

  const activeRegistrations =
    studentProfile?.student?.RegistrationWrappers?.map(({ RegistrationInfo }) => RegistrationInfo)?.filter(
      (r) => [REG_STATUS.ACTIVE, REG_STATUS.PENDING_START].includes(r?.Status || '') && r.Language === 'English'
    ) || []

  const highestPlacedLevel = Math.max(
    0,
    ...activeRegistrations.map((r) => {
      const lvl = parseInt((r?.LanguageLevel as unknown as string) || '0', 10)
      return Number.isNaN(lvl) ? 0 : lvl
    })
  )

  // Hide Skills Tutor tab for 2B students
  const canShowSkillsTutor =
    hasSkillTutorPermission && highestPlacedLevel >= MATERIAL_LEVEL.SKILLS_TUTOR_MIN && !studentProfile.is2bStudentOnly
  const advLpIds = Object.entries(studentProfile.MaterialsISC)
    .filter(([, v]) => Number(v.MaterialLevel) > MATERIAL_LEVEL.SKILLS_TUTOR_BASIC)
    .map(([k]) => k)
    .pop()
  const basicLpIds = Object.entries(studentProfile.MaterialsISC)
    .filter(([, v]) => Number(v.MaterialLevel) < MATERIAL_LEVEL.SKILLS_TUTOR_ADVANCED)
    .map(([k]) => k)
    .pop()
  const lpids = [basicLpIds, advLpIds].filter(Boolean) as string[]
  const lookup = useMemo(() => {
    const isc =
      studentProfile.EncodedDistinctPartialNames?.filter((name) =>
        studentProfile.LPMaterialsForISC.filter((mat) => lpids.includes(mat.LPID))?.find(
          (mat) => mat.partialName == atob(name)
        )
      ) || []
    const stc = studentProfile.EncodedDistinctSTCNames || []

    // Merge both arrays
    const allNames = [...isc, ...stc]

    // Create a lookup where each name gets a tab index (starting from 2)
    return allNames.reduce((p, c, index) => {
      const key = `#${c}`
      p[key] = index + 2
      return p
    }, {})
  }, [lpids, studentProfile.EncodedDistinctPartialNames, studentProfile.EncodedDistinctSTCNames])

  const [activeTab, setActiveTab] = useState(lookup[location.hash] || 1)
  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(lookup[location.hash] || 1)
    }
    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [lookup])

  const handleTabClick = (hash: string) => {
    window.location.hash = hash
    localStorage.setItem(`lp:${studentProfile.SFId}:activeHash`, hash)
  }

  const getKeyByValue = (obj, value) => {
    return Object.keys(obj).find((key) => obj[key] === value)
  }
  return (
    <>
      <TabWrapper>
        <MenuTab activeTab={activeTab}>
          <>
            <MenuTabItem>
              <TabItemWrapper onClick={() => handleTabClick('')}>
                <FormattedMessage id="Main courses" defaultMessage="Main courses" />
              </TabItemWrapper>
            </MenuTabItem>
            {hasActiveIndSpecificContent &&
              isStudent &&
              studentProfile.EncodedDistinctPartialNames.filter((name) => lookup[`#${name}`]).map((i) => {
                const iscTabName = atob(i)
                  ?.trim()
                  .replace(/(?:\s+\S+){1}$/, '')
                  .trim()
                return (
                  <MenuTabItem key={i}>
                    <TabItemWrapper onClick={() => handleTabClick(i)}>
                      <FormattedMessage id={`${iscTabName}`} defaultMessage={`${iscTabName}`} />
                    </TabItemWrapper>
                  </MenuTabItem>
                )
              })}
            {isStudent &&
              canShowSkillsTutor &&
              studentProfile.EncodedDistinctSTCNames.map((i) => (
                <MenuTabItem key={i}>
                  <TabItemWrapper onClick={() => handleTabClick(i)}>
                    <FormattedMessage id={`Skills Tutor`} defaultMessage={`Skills Tutor`} />
                  </TabItemWrapper>
                </MenuTabItem>
              ))}
          </>
        </MenuTab>
      </TabWrapper>
      <Spacer size="lg" />
      <>
        {activeTab === 1 && (
          <QueryLearningPath level="all">
            {({ loading, error, LearningPaths, is2BRestrictedLP }) => {
              return loading ? (
                <Loader />
              ) : (
                <LPContextProvider>
                  <Layout
                    key={LearningPaths?.learningPaths?.length}
                    error={error}
                    LearningPaths={LearningPaths}
                    activeDropdownLPId={selected}
                    activeDropdownRegistrationId={selectedRegistrationId}
                    activeDropdownProgramId={selectedProgramId}
                    is2BRestrictedLP={is2BRestrictedLP}
                  />
                </LPContextProvider>
              )
            }}
          </QueryLearningPath>
        )}
        {studentProfile.EncodedDistinctPartialNames.find((name) => lookup[`#${name}`] === activeTab) && (
          <QueryLearningPathISC
            level="all"
            lpids={lpids.filter(
              (id) =>
                id ==
                studentProfile.LPMaterialsForISC.find(
                  (mat) => mat.partialName === atob(getKeyByValue(lookup, activeTab)?.slice(1) ?? '')
                )?.LPID
            )}
          >
            {({ loading, error, LearningPaths }) => {
              return loading ? (
                <Loader />
              ) : (
                <LPContextProvider>
                  <MyBerlitzTutorLayout
                    key={LearningPaths?.learningPaths?.length}
                    error={error}
                    LearningPaths={LearningPaths}
                    activeDropdownLPId={selected}
                  />
                </LPContextProvider>
              )
            }}
          </QueryLearningPathISC>
        )}
        {canShowSkillsTutor &&
        studentProfile.EncodedDistinctSTCNames.find((name) => lookup[`#${name}`] === activeTab) ? (
          <SkillsTutorTab />
        ) : null}
      </>
    </>
  )
}

export default ParentLayout
